import React, { useEffect, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    updateAllAdminSettings,
    IAdminSettingsState,
} from "@features/adminSettingsSlice";
import ToggleSwitch from "@buttons/ToggleSwitch";
import {
    Features,
    IAccess,
    IClientAccess,
    IAdminToggleBtn,
    Platforms,
    IReportsData,
    ParentModule,
    SubModule,
    TActiveClientId,
    TAdminSettings,
    TUserdata,
    TReportsData,
    AdminSettings,
    AdminSettingsAction,
} from "@ts/common-types";
import {
    updateShowAdminSettingsDisplay,
    updateConfirmModalDisplay,
} from "@features/modalSlice";
import { showToast } from "@ts/modals";
import {
    getClientChannels,
    getClientReports,
    updateAdminSettings,
} from "@ts/retrieve-data";
import UserSettingsModal from "./UserSettingsModal";
import { ReactComponent as SettingsIcon } from "@images/settings_icon.svg";
import { ReactComponent as UserSettingsIcon } from "@images/user_gear_solid.svg";
import * as Sentry from "@sentry/react";
import ReportsSettingsModal from "./ReportsSettingsModal";
import { useNavigate } from "react-router-dom";
import { getActiveModule, updateChannelSettings } from "@ts/utils";
import store from "@/store";
import usePermissionCheck from "@/hooks/usePermissionCheck";
import useGetModulesData from "@/hooks/useGetModulesData";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import {
    ADMIN_SETTINGS_CONFIG,
    AdminSettingConfig,
} from "@/config/admin-config";
import { ToggleOptions } from "@ts/common-types";

interface IAdminSettings {
    showAdminSettings: boolean;
}

const AdminSettingsModal: React.FC<IAdminSettings> = ({
    showAdminSettings,
}) => {
    const { showReviewAnalyticsFeature, showProductAnalysisFeature } =
        useFeatureFlags();
    const { activeModule } = getActiveModule();
    const navigate = useNavigate();

    const adminSettings = useSelector(
        (state: { adminSettings: { value: IAdminSettingsState } }) =>
            state.adminSettings.value
    );

    const {
        dayparting: enableDayparting,
        buybox: enableBuybox,
        revstack: enableRevstack,
        keywordRecommendation: enableKeywordRecommendation,
        navigator: enableNavigator,
        categoryAnalysis: enableCategoryAnalysis,
        productAnalysis: enableProductAnalysis,
    } = adminSettings;

    const [addUsersDisplay, setAddUsersDisplay] = useState(false);
    const [addReportsDisplay, setAddReportsDisplay] = useState(false);
    const [existingReportsData, setExistingReportsData] =
        useState<Array<IReportsData>>();
    const [getUpdatedReports, setGetUpdatedReports] = useState<boolean>(false);
    const dispatch = useDispatch();
    const activeClientId: number = useSelector(
        (state: TActiveClientId) => +state.activeClientId.value
    );

    const userData = useSelector((state: TUserdata) => state.userData.value);

    const userAccess = userData?.access?.find((access: IAccess) =>
        access.clients.find(
            (client: IClientAccess) => client.client_id === activeClientId
        )
    );

    const platformAccess = userAccess
        ? userAccess?.platforms.map((item: string) => item.toLowerCase())
        : [];

    const userHasActivationAccess =
        userData?.is_super_admin ||
        platformAccess.includes(Platforms.ACTIVATION);

    const ADMIN_SETTINGS_MAP: Record<keyof IAdminSettingsState, AdminSettings> =
        {
            dayparting: AdminSettings.DAY_PARTING,
            buybox: AdminSettings.BUYBOX,
            revstack: AdminSettings.REVSTACK,
            keywordRecommendation: AdminSettings.KEYWORD_RECOMMENDATION,
            navigator: AdminSettings.NAVIGATOR,
            categoryAnalysis: AdminSettings.CATEGORY_ANALYSIS,
            productAnalysis: AdminSettings.PRODUCT_ANALYSIS,
        };

    const userHasPlanningAccess =
        userData?.is_super_admin || platformAccess.includes(Platforms.PLANNING);
    const { canListReports } = usePermissionCheck();

    const platformFeatures = useGetModulesData({ type: "platform-features" });

    const reportsData = useSelector(
        (state: TReportsData) => state.reportsData.value
    );

    const isFirstRender = useRef(true);

    function adminSettingsDraftReducer(
        state: IAdminSettingsState,
        action: AdminSettingsAction
    ): IAdminSettingsState {
        switch (action.type) {
            case ToggleOptions.TOGGLE:
                return {
                    ...state,
                    [action.key]: !state[action.key],
                };

            case ToggleOptions.RESET:
                return action.payload;

            default:
                return state;
        }
    }

    const [draftSettings, draftDispatch] = useReducer(
        adminSettingsDraftReducer,
        adminSettings
    );

    const initialRef = useRef<IAdminSettingsState>(adminSettings);
    const initialAdminSettings = useRef(
        useSelector(
            (state: { adminSettings: { value: IAdminSettingsState } }) =>
                state.adminSettings.value
        )
    );
    const currentSettings = useSelector(
        (state: { adminSettings: { value: IAdminSettingsState } }) =>
            state.adminSettings.value
    );

    const hasChanges = Object.keys(draftSettings).some(
        (key) =>
            draftSettings[key as keyof IAdminSettingsState] !==
            initialAdminSettings.current[key as keyof IAdminSettingsState]
    );

    useEffect(() => {
        if (showAdminSettings) {
            initialRef.current = adminSettings;
            draftDispatch({ type: "RESET", payload: adminSettings });
        }
    }, [showAdminSettings, adminSettings]);

    useEffect(() => {
        const fetchReports = async () => {
            try {
                if (!canListReports) {
                    setExistingReportsData([]);
                } else if (
                    reportsData &&
                    reportsData.revstack &&
                    reportsData.common
                ) {
                    setExistingReportsData([
                        ...reportsData.revstack,
                        ...reportsData.common,
                    ]);
                } else {
                    setExistingReportsData([]);
                }
            } catch (err) {
                showToast("danger", "Unable to get reports data");
            }
        };

        fetchReports();
    }, [enableRevstack, canListReports, activeClientId, reportsData]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const fetchClientReports = async () => {
            await store.dispatch(getClientReports());
        };

        fetchClientReports();
    }, [getUpdatedReports]);

    useEffect(() => {
        resetToDefaultSettings();
    }, [
        enableDayparting,
        enableBuybox,
        enableRevstack,
        enableKeywordRecommendation,
        enableNavigator,
        enableCategoryAnalysis,
        enableProductAnalysis,
    ]);

    useEffect(() => {
        return () => {
            dispatch(updateShowAdminSettingsDisplay(false));
            resetToDefaultSettings();
        };
    }, []);

    const resetToDefaultSettings = () => {
        dispatch(updateShowAdminSettingsDisplay(false));
    };

    const closeAdminSettingsModal = () => {
        dispatch(updateShowAdminSettingsDisplay(false));
    };

    const canRenderSetting = (config: AdminSettingConfig) => {
        if (!platformFeatures.includes(config.feature)) return false;
        if (config.requiresActivationAccess && !userHasActivationAccess)
            return false;
        if (config.requiresPlanningAccess && !userHasPlanningAccess)
            return false;
        if (config.requiresReviewAnalytics && !showReviewAnalyticsFeature)
            return false;
        if (config.requiresProductAnalysis && !showProductAnalysisFeature)
            return false;
        return true;
    };

    const TOGGLE_ID_TO_KEY: Record<string, keyof IAdminSettingsState> = {
        [Features.DAY_PARTING]: "dayparting",
        [Features.BUYBOX]: "buybox",
        [Features.REVSTACK]: "revstack",
        [Features.KEYWORD_RECOMMENDATIONS]: "keywordRecommendation",
        [Features.NAVIGATOR]: "navigator",
        [Features.CATEGORY_ANALYSIS]: "categoryAnalysis",
        [Features.PRODUCT_ANALYSIS]: "productAnalysis",
    };

    const onToggleChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const key = TOGGLE_ID_TO_KEY[e.target.id];

        if (!key) return;

        draftDispatch({ type: "TOGGLE", key });
    };

    const performSave = async (updatedSettings: TAdminSettings) => {
        try {
            const updatedSettingsKeys = Object.keys(updatedSettings);
            if (updatedSettingsKeys.length !== 0) {
                const response = await updateAdminSettings(updatedSettings);
                if (!response) {
                    throw new Error(response.message);
                }
                updatedSettingsKeys.includes(AdminSettings.REVSTACK) &&
                    !updatedSettings.revstack &&
                    activeModule === ParentModule.REVSTACK &&
                    navigate(`/${ParentModule.PERFORMANCE_OVERVIEW}`);
                updatedSettingsKeys.includes(
                    AdminSettings.KEYWORD_RECOMMENDATION
                ) &&
                    !updatedSettings[AdminSettings.KEYWORD_RECOMMENDATION] &&
                    [
                        SubModule.KEYWORD_AUTO,
                        SubModule.KEYWORD_MANUAL,
                        SubModule.KEYWORD_REVINITI,
                    ].includes(activeModule as SubModule) &&
                    navigate(`/${ParentModule.PERFORMANCE_OVERVIEW}`);
                dispatch(updateAllAdminSettings(draftSettings));

                await store.dispatch(getClientChannels());
                updateChannelSettings();
                showToast(
                    `${response.status ? "success" : "danger"}`,
                    response.message
                );
                window.location.reload();
            }
        } catch (err) {
            console.error(err);
            Sentry.captureException(err);
        }
    };

    const onSaveClickHandler = async () => {
        const updatedSettings: TAdminSettings = {};

        (
            Object.keys(ADMIN_SETTINGS_MAP) as (keyof IAdminSettingsState)[]
        ).forEach((key) => {
            if (draftSettings[key] !== adminSettings[key]) {
                updatedSettings[ADMIN_SETTINGS_MAP[key]] = draftSettings[key];
            }
        });

        if (Object.keys(updatedSettings).length === 0) return;

        if (updatedSettings[AdminSettings.DAY_PARTING] === false) {
            dispatch(
                updateConfirmModalDisplay({
                    value: true,
                    title: "Disable Dayparting",
                    message:
                        "This will disable dayparting across all campaigns! All of the current campaign schedules will be saved. To use dayparting again in the future, it'll have to be manually re-enabled for the desired campaigns. Are you sure you want to continue?",
                    onConfirm: async () => {
                        await performSave(updatedSettings);
                    },
                })
            );
            return;
        }

        await performSave(updatedSettings);
    };

    return (
        <>
            <div>
                <div className="card bg-light admin-settings-container">
                    {ADMIN_SETTINGS_CONFIG.map((config) => {
                        if (!canRenderSetting(config)) return null;

                        return (
                            <div
                                key={config.key}
                                className="admin-settings-components"
                            >
                                <label>{config.label}</label>
                                <ToggleSwitch
                                    id={config.toggleId}
                                    onToggleChange={onToggleChangeHandler}
                                    size="0.8rem"
                                    checked={draftSettings[config.key]}
                                />
                            </div>
                        );
                    })}

                    {/* add report and add user*/}
                    <>
                        <div className="admin-settings-components">
                            <label>Reports Configuration</label>
                        </div>
                        {/* add user */}
                        <div
                            className="reset-admin-settings ms-3"
                            style={{ marginTop: "-5px" }}
                        >
                            <div className="admin-settings-components ms-3">
                                <div>
                                    <label className="admin-settings-subcomponent">
                                        {" "}
                                        User Settings
                                    </label>
                                </div>
                                <UserSettingsIcon
                                    className="me-2"
                                    role="button"
                                    fill="#5c5e63"
                                    height="20"
                                    width="20"
                                    onClick={() => setAddUsersDisplay(true)}
                                />
                            </div>
                        </div>
                        {/* add reports */}

                        <div
                            className="reset-admin-settings ms-3"
                            style={{ marginTop: "-7px" }}
                        >
                            <div className="admin-settings-components ms-3">
                                <div>
                                    <label className="admin-settings-subcomponent">
                                        {" "}
                                        Reports Settings
                                    </label>
                                </div>
                                <SettingsIcon
                                    className="me-2"
                                    role="button"
                                    fill="#5c5e63"
                                    height="20"
                                    width="20"
                                    onClick={() => setAddReportsDisplay(true)}
                                />
                            </div>
                        </div>
                    </>

                    <div className="reset-admin-settings">
                        <div className="admin-settings-components">
                            <div>
                                <label className="d-block">
                                    {" "}
                                    Reset Settings
                                </label>
                                <label className="d-block text-secondary reset-admin-settings-label">
                                    Restore settings to their original defaults
                                </label>
                            </div>
                            <button
                                className="btn btn-sm"
                                style={{ backgroundColor: "#D8D8D8" }}
                                onClick={resetToDefaultSettings}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mt-3 float-end">
                    <button
                        className="btn btn-sm btn-secondary me-2"
                        onClick={closeAdminSettingsModal}
                    >
                        Cancel
                    </button>
                    <button
                        id="save-settings-btn"
                        className="btn btn-sm btn-primary"
                        onClick={onSaveClickHandler}
                        disabled={!hasChanges}
                    >
                        Save Changes
                    </button>
                </div>
            </div>
            <UserSettingsModal {...{ addUsersDisplay, setAddUsersDisplay }} />
            <ReportsSettingsModal
                {...{
                    addReportsDisplay,
                    setAddReportsDisplay,
                    existingReportsData,
                    setGetUpdatedReports,
                }}
            />
        </>
    );
};

export default AdminSettingsModal;
